import { useCreateRating } from '../../../../hooks/lobbies/useCreateRating'
import { useEffect, useState } from 'react'
import { AddReviewRequest } from '../../../../types/lobbies/requests/post-add-a-review-request'
import { StaticImageData } from 'next/image'
import IconPerformance from '../../../../public/static/rating/icon_performance.svg'
import IconBehavior from '../../../../public/static/rating/icon_behavior.svg'
import IconCommunication from '../../../../public/static/rating/icon_communication.svg'
import IconAccountIssues from '../../../../public/static/rating/icon_account_issues.svg'
import IconTechnicalIssues from '../../../../public/static/rating/technical_issues.svg'

export type Option = {
  id: number
  title: string
  about: string
  placeholder?: string
  allowInput: boolean
  checked?: boolean
  input?: string | null
}

export interface Tag {
  id: string
  name: string
  checked: boolean
}

export interface Category {
  id: string
  name: string
  image?: StaticImageData
  tags: Tag[]
}
type Categories = Category[]
export default function useFeedback(requestId?: number, teammateId?: number) {
  const categories: Categories = [
    {
      id: 'performance',
      name: 'Performance',
      image: IconPerformance,
      tags: [
        { id: 'poor_gameplay', name: 'Poor gameplay', checked: false },
        { id: 'lost_game', name: 'Lost the game', checked: false },
        { id: 'slow_reaction', name: 'Slow reaction times', checked: false },
        {
          id: 'bad_decision_making',
          name: 'Bad decision making',
          checked: false,
        },
        { id: 'missed_objectives', name: 'Missed objectives', checked: false },
        {
          id: 'took_too_long',
          name: 'Took too long to start the game',
          checked: false,
        },
        {
          id: 'went_afk',
          name: 'Went AFK',
          checked: false,
        },
      ],
    },
    {
      id: 'behavior',
      name: 'Behavior',
      image: IconBehavior,
      tags: [
        {
          id: 'rude_or_unprofessional',
          name: 'Rude or unprofessional',
          checked: false,
        },
        {
          id: 'unfriendly_attitude',
          name: 'Unfriendly attitude',
          checked: false,
        },
        {
          id: 'was_not_nice',
          name: 'Was not nice to other teammates in game',
          checked: false,
        },
        {
          id: 'blamed_me',
          name: 'Blamed me',
          checked: false,
        },
        {
          id: 'distracted_during_gameplay',
          name: 'Distracted during gameplay',
          checked: false,
        },
      ],
    },
    {
      id: 'communication',
      name: 'Communication',
      image: IconCommunication,
      tags: [
        { id: 'lost_game', name: 'Poor communication', checked: false },
        { id: 'slow_reaction', name: 'Bad at communicating', checked: false },
        {
          id: 'difficult_to_understand',
          name: 'Difficulty understanding accent',
          checked: false,
        },
        {
          id: 'poor_quality_audio',
          name: 'Poor quality audio',
          checked: false,
        },
      ],
    },
    {
      id: 'technical_issues',
      name: 'Technical Issues',
      image: IconTechnicalIssues,
      tags: [
        { id: 'bad_ping', name: 'Bad ping (internet lag)', checked: false },
        {
          id: 'disconnected_frequently',
          name: 'Disconnected frequently',
          checked: false,
        },
      ],
    },
    {
      id: 'account_issues',
      name: 'Account Issues',
      image: IconAccountIssues,
      tags: [
        {
          id: 'took_too_long_to_find_account',
          name: 'Took too long to find account ',
          checked: false,
        },
        {
          id: 'wrong_character_champion_picked',
          name: 'Wrong character/champion picked',
          checked: false,
        },
        {
          id: 'account_banned_or_logged_in_on_during_gameplay',
          name: 'Account got banned or logged in on during gameplay',
          checked: false,
        },
        {
          id: 'account_in_high_elo',
          name: 'Account was in too high elo',
          checked: false,
        },
      ],
    },
  ]

  const Options: Option[] = [
    {
      id: 1,
      title: "Teammate didn't invite me in-game quickly",
      about: "Teammate didn't invite me in-game quickly",
      allowInput: false,
    },
    {
      id: 2,
      title: 'Teammate did not perform well in-game',
      about: 'Teammate did not perform well in-game.',
      allowInput: false,
    },
    {
      id: 3,
      title: 'Teammate was rude',
      about: 'Teammate was rude',
      allowInput: false,
    },
    {
      id: 4,
      title: 'Teammate refused to voice',
      about: 'Teammate refused to voice',
      allowInput: false,
    },
    {
      id: 5,
      title: 'Teammate took my in-game role',
      about: 'Teammate took my in-game role',
      allowInput: false,
    },
    {
      id: 6,
      title: 'Teammate was not social',
      about: 'Teammate was not social',
      allowInput: false,
    },
    {
      id: 7,
      title: 'Teammate had poor language skills',
      about: 'Teammate had poor language skills',
      allowInput: false,
    },
    {
      id: 8,
      title: 'Other',
      about: 'Other',
      allowInput: false,
      placeholder: 'Tell us more...',
    },
  ]
  const { mutate, isSuccess } = useCreateRating()
  const [checkBoxes, setCheckBoxes] = useState<Option[] | null | undefined>(
    Options,
  )
  const [ratingValues, setRatingValues] = useState<AddReviewRequest>({
    teammateId: teammateId,
    rating: 0,
    requestId: requestId ?? -1,
    comment: '',
  })
  const [categoriesData, setCategoriesData] = useState(categories)
  const otherOpenState = useState(false)
  const [isOpenOther, setIsOpenOther] = otherOpenState

  const [comment, setComment] = useState<string>('')
  const handleRatingChange = (newValue: number) => {
    setRatingValues((prev) => ({ ...prev, rating: newValue, comment: '' }))
  }

  const handleCheckBoxChange = (id: number) => {
    setCheckBoxes((prevCheckBoxes) => {
      return prevCheckBoxes?.map((checkBox) => {
        return {
          ...checkBox,
          checked: checkBox.id === id ? !checkBox.checked : false,
        }
      })
    })
  }

  useEffect(() => {
    if (ratingValues.rating) {
      if (ratingValues.comment === 'Other') {
        setIsOpenOther(true)
        return
      }
      if (ratingValues.rating <= 4) {
        const checkedTagsNames = categoriesData
          .flatMap((category) => category.tags)
          .filter((tag) => tag.checked)
          .map((tag) => tag.name)

        const checkedTagsNamesString = checkedTagsNames.join(',')
        mutate({ ...ratingValues, comments: checkedTagsNamesString })
      } else {
        mutate({ ...ratingValues })
      }
    }
  }, [ratingValues, categoriesData])

  useEffect(() => {
    setRatingValues((prev) => ({
      ...prev,
      comment: comment ?? '',
    }))
  }, [comment])

  const updateSelectedOptions = () => {
    if (!checkBoxes) {
      return
    }
    const reasons = checkBoxes.find((option) => option.checked)

    setRatingValues((prev) => ({
      ...prev,
      comment: reasons?.title ?? '',
    }))
  }

  useEffect(() => {
    updateSelectedOptions()
  }, [checkBoxes])

  const handleTagToggle = (categoryId: string, tagId: string) => {
    const newData = categoriesData.map((category) => {
      if (category.id === categoryId) {
        return {
          ...category,
          tags: category.tags.map((tag) => {
            if (tag.id === tagId) {
              return { ...tag, checked: !tag.checked }
            }
            return tag
          }),
        }
      }
      return category
    })
    setCategoriesData(newData)
  }

  return {
    otherOpenState,
    isOpenOther,
    checkBoxes,
    ratingValues,
    categoriesData,
    setComment,
    handleRatingChange,
    handleCheckBoxChange,
    handleTagToggle,
  }
}
